/**
 * Pure home-mode resolution. The home page is modal (DESIGN §10): what it shows is
 * a function of the release facts, not of what the operator last clicked. This
 * module turns the two host documents and the release documents from *both*
 * databases into the same `ReleaseFacts` the hosts feed `phaseFrom`, so the browser
 * and the hosts agree on the phase by import — kept pure and React-free so it is
 * unit-testable.
 *
 * Two databases, because a release is two halves and each host writes its own. The
 * preparing facts come from `prepare/releases/{version}`; the deploying ones from
 * `releases/{version}` on `(default)`, its instructions, and — for `releaseSynced`,
 * which is per-boot — the release host's own heartbeat. Every fact that *either* half
 * can observe is read from either, and that redundancy is the point:
 *
 *  - `published` is true once the prepare host's publish step is done *or* a
 *    `(default)` release document exists. The release host projects one the first
 *    time it syncs, so a rebuilt prepare host — or one whose document was lost —
 *    does not walk a published release back to the review gate.
 *  - `closed` is true once a release document says so *or* the prepare host has
 *    seen `deployed/server` cover the tag. The second is what makes the interop
 *    window work: a release the laptop deployed and closed leaves this page too,
 *    even though no `release.close` instruction ever ran (DESIGN §10's milestone
 *    table, principle 5).
 */
import {
    INSTRUCTION_KIND,
    isInflightStatus,
    phaseFrom,
    PREPARING_PHASES,
    DEPLOYING_PHASES,
    type HostDoc,
    type InstructionDoc,
    type Phase,
    type PrepareReleaseDoc,
    type ReleaseDoc,
    type ReleaseFacts,
} from '@finvari/deployer';
import type { SystemView } from './releaseSystem';

export type PrepareRelease = PrepareReleaseDoc & { id: string };

/** A `(default)` release document — the deploy half's projection of the same release. */
export type DeployRelease = ReleaseDoc & { id: string };

/**
 * As much of a prepare-host instruction as the phase depends on. Narrow so the
 * callers can pass their recent-instruction rows and the tests can pass literals.
 */
export type PrepareActivity = Pick<InstructionDoc, 'kind' | 'status'>;

/**
 * The single active preparation release, if any. Only one runs at a time, so this
 * is the first non-aborted `preparing` document; if several somehow exist the
 * latest version wins so a stale aborted-then-restarted doc never shadows it.
 */
export function activePrepareRelease(releases: PrepareRelease[]): PrepareRelease | undefined {
    const live = releases.filter((r) => !r.abortedAt);
    if (live.length <= 1) return live[0];
    return [...live].sort((a, b) => compareVersion(b.version, a.version))[0];
}

/**
 * The release the deploying half is about: the newest `(default)` document that is
 * not closed.
 *
 * Newest-not-closed rather than "the one matching the active prepare release",
 * because the two collections can legitimately disagree about which release exists.
 * A release closed by the laptop has a `(default)` document the release host never
 * saw close, and one the release host is deploying may have no prepare document at
 * all if that host was rebuilt. Whichever is open is the one to show.
 */
export function activeDeployRelease(releases: DeployRelease[]): DeployRelease | undefined {
    const open = releases.filter((r) => r.phase !== 'closed');
    if (open.length <= 1) return open[0];
    return [...open].sort((a, b) => compareVersion(b.version, a.version))[0];
}

/** Compare `v1.NNN` versions numerically (not lexically, so v1.99 < v1.101). */
export function compareVersion(a: string, b: string): number {
    return buildNumber(a) - buildNumber(b);
}

function buildNumber(version: string): number {
    const m = version.match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : 0;
}

const isOnline = (host: HostDoc | undefined): boolean => host?.status === 'online';
const stepDone = (release: PrepareRelease | undefined, step: keyof PrepareReleaseDoc['steps']): boolean =>
    release?.steps?.[step] === 'done';

/**
 * Approval is the point of no return, and the half minute it takes on the host
 * (DVC push, notes commit, branch push, tag push) is the one window in which the
 * git-derived facts still read `reviewing` — the tag does not exist yet, so
 * nothing on the release document has changed. An in-flight approval is therefore
 * read as the machine's `publishing` fact: the phase moves the moment the
 * instruction is created, and the legality table freezes the announcement and the
 * step actions for the whole round trip instead of leaving them live to be edited,
 * re-approved, or aborted while the release is being tagged.
 */
function isPublishing(instructions: PrepareActivity[]): boolean {
    return instructions.some(
        (i) => i.kind === INSTRUCTION_KIND.prepareApproveAnnouncement && isInflightStatus(i.status),
    );
}

/**
 * `release.close` is in flight. The same window `isPublishing` covers, at the other
 * end of the release: the close moves `deployed/server` and pushes it, and until
 * that push lands every git-derived fact still reads `open`. Reading the in-flight
 * instruction as the `closing` fact freezes the deploy actions for the round trip
 * rather than leaving Close live to be pressed twice.
 */
function isClosing(instructions: PrepareActivity[]): boolean {
    return instructions.some((i) => i.kind === INSTRUCTION_KIND.releaseClose && isInflightStatus(i.status));
}

/**
 * Everything the home page observes, in one argument. Grouped rather than passed as
 * six positional parameters because both halves of a release contribute and the
 * order of six booleans' worth of documents is not something a call site should have
 * to get right.
 */
export interface HomeInput {
    prepare: PrepareRelease | undefined;
    /**
     * Every `(default)` release document, not the one `activeDeployRelease` picks.
     * The `closed` fact is observed on a *closed* document, and that selector keeps
     * only the open ones — so a pre-selected release hands this function exactly the
     * documents that cannot answer it, and `closed` falls back to the prepare host's
     * `deployed` alone. That is how v1.1264 sat at `release_host_starting` for a
     * release the release host had already closed: the prepare host was stopped
     * before the close, so its copy of the fact was half an hour stale and the
     * deploy-side copy had been filtered away.
     */
    deployReleases: DeployRelease[];
    prepareHost: HostDoc | undefined;
    releaseHost: HostDoc | undefined;
    /** The recent-instruction window per host, for the two mid-flight facts. */
    prepareInstructions: PrepareActivity[];
    releaseInstructions: PrepareActivity[];
}

/**
 * Build the release facts the state machine maps to a phase. When neither half has a
 * release the only fact that matters is `releaseOpen: false`, which `phaseFrom` maps
 * to `idle`.
 */
export function homeFacts(input: HomeInput): ReleaseFacts {
    const { prepare } = input;
    const deploy = activeDeployRelease(input.deployReleases);
    // The release in front of the operator, which is the one whose closure ends this
    // page: the open deploy document if there is one, otherwise whatever the prepare
    // half is about.
    const inView = deploy?.version ?? prepare?.version;
    // Matched by version rather than "any closed document", so a closed v1.1264 does
    // not read as the closure of the v1.1265 being prepared behind it.
    const deployClosed = !!inView && input.deployReleases.some((r) => r.version === inView && r.phase === 'closed');
    return {
        releaseOpen: !!prepare || !!deploy,
        // From the prepare side "published" is the publish step being done (the tag
        // has been pushed); from the deploy side it is the release document existing
        // at all, since the release host only projects one for a tag it can see.
        published: stepDone(prepare, 'publish') || !!deploy,
        // A release document never leaves either collection, so without this fact
        // every published release would stay open forever: the page would sit at
        // `release_host_starting` and refuse the next start as "a release is already
        // open", even though git says the release shipped and the next one is due.
        closed: deployClosed || !!prepare?.deployed,
        prepareHostOnline: isOnline(input.prepareHost),
        releaseHostOnline: isOnline(input.releaseHost),
        merged: stepDone(prepare, 'merge'),
        captured: stepDone(prepare, 'capture'),
        described: stepDone(prepare, 'describe'),
        publishing: isPublishing(input.prepareInstructions),
        closing: isClosing(input.releaseInstructions),
        // The release host's own fact, and the reason the deploying page can be
        // trusted: until a sync of *this boot* has run, the checkout is whatever the
        // disk snapshot held and the plan on the release document describes files that
        // are not there (DESIGN §10). Read from the heartbeat rather than from that
        // plan, because `reconcile` recomputes the plan after every instruction — a
        // doctor run on a just-booted host would otherwise pass for a sync.
        releaseSynced: !!deploy && input.releaseHost?.syncedRelease === deploy.version,
    };
}

/** The current phase for the home page, from the observed documents. */
export function homePhase(input: HomeInput): Phase {
    return phaseFrom(homeFacts(input));
}

/**
 * Whether the home page shows the guided preparation flow rather than the idle
 * dashboard. It needs an active prepare document *and* a preparing phase; the
 * hand-off to the deploy half is `showsDeployingView` from `release_host_starting`
 * on. The debug snapshot reports both answers, so an operator can see which input
 * put the page in the mode it is in.
 */
export function showsPreparingView(release: PrepareRelease | undefined, phase: Phase): boolean {
    return !!release && PREPARING_PHASES.includes(phase);
}

/**
 * Whether the home page shows the deploying dashboard.
 *
 * Keyed on the phase alone, not on the `(default)` document existing: the first of
 * the deploying phases is `release_host_starting`, which is precisely the state where
 * the release host has not written that document yet — because it is off. The view
 * renders its table empty and offers the Start and Sync actions, which is the whole
 * content of that phase.
 */
export function showsDeployingView(phase: Phase): boolean {
    return DEPLOYING_PHASES.includes(phase);
}

/** Whether each of the documents the phase is derived from has been heard from. */
export interface HomeSourcesLoading {
    prepareHost: boolean;
    releaseHost: boolean;
    prepareReleases: boolean;
    deployReleases: boolean;
}

/**
 * Whether any document the phase is derived from is still unread. Named rather
 * than spelled out at each call site because the page and the debug snapshot must
 * answer it from the same set: a snapshot built from fewer sources reports a view
 * the operator was not looking at.
 */
export function homeSourcesLoading(loading: HomeSourcesLoading): boolean {
    return loading.prepareHost || loading.releaseHost || loading.prepareReleases || loading.deployReleases;
}

/**
 * Whether the home page is still reading rather than showing any of its three
 * views — the spinner, not the flow and not the sleeping panel.
 *
 * The system's state belongs in the answer beside the documents: an unread host
 * document is what `asleep` looks like too, so a page that gated on the documents
 * alone would offer a wake for a system that is already up.
 */
export function showsLoadingView(sourcesLoading: boolean, system: SystemView): boolean {
    return sourcesLoading || system.state === 'loading';
}
